import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import LockedTool from "@/components/LockedTool";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkles, Copy, Mail, Loader2 } from "lucide-react";

const RECIPIENTS = ["Twitch / Kick Streamer", "YouTube Content Creator", "Gaming Journalist / Press"];

const ColdEmail = () => {
  const { user } = useAuth();
  const [gameName, setGameName] = useState("");
  const [pitch, setPitch] = useState("");
  const [link, setLink] = useState("");
  const [recipient, setRecipient] = useState(RECIPIENTS[0]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (!gameName.trim() || !pitch.trim()) {
      toast.error("Add a game name and your key hook first.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post("/generate/cold-email", {
        game_name: gameName,
        pitch,
        link,
        recipient,
      });
      setResult(res.data);
      toast.success("Cold email generated & saved to history.");
    } catch (e) {
      toast.error("Generation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard!`);
    } catch {
      toast.error("Couldn't access the clipboard.");
    }
  };

  if (!user?.is_pro) {
    return (
      <Layout title="Streamer & PR Cold Email Pitcher" subtitle="Tool 03 // Land coverage without the awkward outreach">
        <LockedTool toolName="Streamer & PR Cold Email Pitcher" />
      </Layout>
    );
  }

  return (
    <Layout title="Streamer & PR Cold Email Pitcher" subtitle="Tool 03 // Land coverage without the awkward outreach">
      <div className="grid lg:grid-cols-2 gap-6" data-testid="cold-email-module">
        {/* Input */}
        <div className="border border-zinc-800 bg-zinc-900/30 rounded-2xl p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4">Pitch Details</p>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-300 mb-2 block">Game Name</label>
              <Input
                value={gameName}
                onChange={(e) => setGameName(e.target.value)}
                data-testid="cold-game-name-input"
                placeholder="e.g. Void Drifter"
                className="bg-zinc-950 border-zinc-800 rounded-xl focus:border-teal-400 focus:ring-1 focus:ring-teal-400"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-300 mb-2 block">Elevator Pitch / Key Hook</label>
              <Textarea
                value={pitch}
                onChange={(e) => setPitch(e.target.value)}
                data-testid="cold-pitch-input"
                placeholder="A roguelike where you pilot a salvaged mech through a dying galaxy — every death rewrites the map."
                className="min-h-[120px] bg-zinc-950 border-zinc-800 rounded-xl text-sm focus:border-teal-400 focus:ring-1 focus:ring-teal-400 resize-y"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-300 mb-2 block">Steam / Trailer Link</label>
              <Input
                value={link}
                onChange={(e) => setLink(e.target.value)}
                data-testid="cold-link-input"
                placeholder="https://store.steampowered.com/app/..."
                className="bg-zinc-950 border-zinc-800 rounded-xl focus:border-teal-400 focus:ring-1 focus:ring-teal-400"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-300 mb-2 block">Target Recipient</label>
              <Select value={recipient} onValueChange={setRecipient}>
                <SelectTrigger data-testid="cold-recipient-select" className="bg-zinc-950 border-zinc-800 rounded-xl focus:border-teal-400 focus:ring-teal-400">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 rounded-xl">
                  {RECIPIENTS.map((r) => (
                    <SelectItem key={r} value={r} className="focus:bg-zinc-800 focus:text-teal-300 rounded-xl">
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <button
            onClick={generate}
            disabled={loading}
            data-testid="generate-cold-email-btn"
            className="mt-6 w-full flex items-center justify-center gap-2 bg-teal-400 text-zinc-950 font-semibold py-3 rounded-xl hover:bg-teal-300 transition-colors duration-200 disabled:bg-zinc-800 disabled:text-zinc-500 disabled:cursor-not-allowed active:scale-[0.99]"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Generating…
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" /> Generate Pitch
              </>
            )}
          </button>
        </div>

        {/* Output */}
        <div className="border border-zinc-800 bg-zinc-900/30 rounded-2xl p-5" data-testid="cold-email-output">
          {loading ? (
            <div className="space-y-3 pt-2">
              <Skeleton className="h-4 w-1/3 bg-zinc-800 rounded-xl" />
              <Skeleton className="h-10 w-full bg-zinc-800 rounded-xl" />
              <Skeleton className="h-10 w-full bg-zinc-800 rounded-xl" />
              <Skeleton className="h-4 w-1/4 bg-zinc-800 rounded-xl mt-4" />
              <Skeleton className="h-40 w-full bg-zinc-800 rounded-xl" />
            </div>
          ) : result ? (
            <div className="space-y-5">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Subject Line Options</p>
                <div className="space-y-2">
                  {result.subjects.map((s, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-2.5"
                      data-testid="subject-line"
                    >
                      <span className="font-mono text-xs text-teal-300 shrink-0">{i + 1}</span>
                      <span className="text-sm text-zinc-200 flex-1">{s}</span>
                      <button
                        onClick={() => copy(s, "Subject line")}
                        data-testid={`copy-subject-${i}`}
                        className="text-zinc-500 hover:text-teal-300 transition-colors duration-200 shrink-0"
                        title="Copy subject"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Email Body</p>
                  <button
                    onClick={() => copy(result.body, "Email body")}
                    data-testid="copy-email-body-btn"
                    className="flex items-center gap-1.5 text-xs border border-zinc-700 text-teal-300 rounded-xl px-3 py-1.5 hover:border-teal-500/40 hover:bg-teal-400/10 transition-colors duration-200"
                  >
                    <Copy className="h-3.5 w-3.5" /> Copy Email
                  </button>
                </div>
                <pre className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 text-sm text-zinc-200 whitespace-pre-wrap font-sans leading-relaxed max-h-[360px] overflow-auto" data-testid="email-body">
                  {result.body}
                </pre>
              </div>
            </div>
          ) : (
            <div className="h-full min-h-[300px] flex flex-col items-center justify-center text-center">
              <Mail className="h-8 w-8 text-zinc-700 mb-3" />
              <p className="text-zinc-600 text-sm font-mono">Your subject lines and email will appear here.</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ColdEmail;
